/*
 * This code was generated with assistance from GitHub Copilot.
 * De-bundled from react-dxd-components@0.1.33 compiled dist/index.js (webpack module id 57).
 */
import * as React from 'react';
import type { MouseEvent } from 'react';

export interface CorporateNavItem {
  /** URL to link to (href) */
  href?: string;
  /** Optional click handler */
  onClick?: (event: MouseEvent<HTMLAnchorElement>) => void;
  /** Text to be displayed for the link */
  text: string;
}

export interface CorporateNavProps {
  /**
   * Show white logo against blue bar.  Should only be set false if the logo will be
   * displayed in place of a Site or Application name in the SiteNav section
   */
  withLogo?: boolean;
  /**
   * If logo is present - link location for when the logo is clicked.  Should usually
   * point to the home page of the site or application
   */
  logoLink?: string;
  /**
   * If logo is present - the hidden text that will be provided for screen readers to
   * explain where clicking on the logo will take them
   */
  logoLinkText?: string;
  /**
   * Optional - set of links to display on the right side of the CorporateNav section.
   */
  navItems?: CorporateNavItem[] | null;
}

/**
 * If withLogo is true - renders logo with link and hidden link text
 * for accessibility
 */
const CorporateLogo = ({
  withLogo,
  logoLink,
  logoLinkText,
}: Pick<CorporateNavProps, 'withLogo' | 'logoLink' | 'logoLinkText'>) => {
  if (!withLogo) {
    return null;
  }

  return (
    <div className="CorporateNav-logo">
      <a href={logoLink}>
        <span className="u-hiddenVisually">{logoLinkText}</span>
      </a>
    </div>
  );
};

/**
 * Util method for rendering links in the CorporateNavMenu section
 */
const renderNavItems = (items: CorporateNavItem[]) =>
  items.map((item) => (
    <a key={item.text} href={item.href} onClick={item.onClick}>
      {item.text}
    </a>
  ));

/**
 * If menu items are present - creates the CorporateNavMenu on the
 * right side of the blue CorporateNav bar
 */
const CorporateNavMenu = ({ items }: { items?: CorporateNavItem[] | null }) => {
  if (!items) {
    return null;
  }

  return <div className="CorporateNav-menu">{renderNavItems(items)}</div>;
};

/**
 * Creates the blue CorporateNav section for the top of the page.
 * Includes the logo by default.  Menu items can be included for display
 * on the right side of the screen.
 *
 * From the Design Guide:
 *
 * > The corporate blue bar is intended to connect a site to the corporate brand.
 * > This area can house links to other universal (broader than the current site) navigation and elements.
 *
 * > The corporate nav's purpose is to brand the site while allowing the site's title to be the primary name on the page. For this reason, do not use the corporate nav by itself.
 *
 * More information on usage is available here: http://design.mutualofomaha.com/digital/templates/header/#corporate-nav
 */
const CorporateNav = ({
  withLogo = true,
  logoLink = '/',
  logoLinkText = 'Mutual of Omaha',
  navItems = null,
}: CorporateNavProps) => (
  <header className="CorporateNav" style={{ height: '40px' }}>
    <div className="CorporateNav-container">
      <CorporateLogo withLogo={withLogo} logoLink={logoLink} logoLinkText={logoLinkText} />
      <CorporateNavMenu items={navItems} />
    </div>
  </header>
);

export default CorporateNav;


